// Cart: localStorage, promo bar, sheet UI
import {
  buildPresetSpecs,
  escapeAttr,
  formatPriceWithRub,
  formatRub,
  parsePriceNumber
} from '../../core/format.js';
import { lazyImageHtml, refreshLazyImages } from '../../core/lazy-image.js';
import { refreshCheckoutOrderSummary } from '../checkout/summary.js';
import { enterCatalogCheckout } from '../checkout/mode.js';
import { showOrderPopover } from '../../app/order-popover.js';
import { preemptTelegramBottomBarForTabbarShow } from '../../core/telegram.js';
import { getLastPresetsList, getPresetsLoadedOnce } from '../presets/list.js';
import { setActiveTab, showView } from '../../app/navigation.js';
import { prepareForCartNavigation, recordCartNavigateGesture } from '../../app/photo-modal.js';
import type { CartLine, ConfigSummaryRow, PresetItem, PresetSpecs } from '../../types/api.js';
import { parseTemplateIdFromCartLineId, presetHasConfigurator } from '../../lib/template-config.js';

const CART_STORAGE_KEY = 'ddpc_cart_v1';

function cartLineTemplateId(line: CartLine): string {
  return String(line.templateId || parseTemplateIdFromCartLineId(String(line.id)));
}

function cartLineFromPreset(item: PresetItem | null | undefined): CartLine | null {
  if (!item || item.id == null) return null;
  const imgs = Array.isArray(item.images) ? item.images.filter(Boolean) : [];
  const img = (item.image && String(item.image)) || (imgs[0] && String(imgs[0])) || '';
  return {
    id: String(item.id),
    title: String(item.title || ''),
    price: String(item.price || ''),
    oldPrice: String(item.oldPrice || ''),
    image: img,
    qty: 1,
    specs: buildPresetSpecs(item.specs ? item.specs : item)
  };
}

function normalizeCartItem(raw: Partial<CartLine> | null | undefined): CartLine | null {
  if (!raw || raw.id == null) return null;
  let qty = parseInt(String(raw.qty), 10);
  if (!Number.isFinite(qty) || qty < 1) qty = 1;
  if (qty > 99) qty = 99;
  const configChoices =
    raw.configChoices && typeof raw.configChoices === 'object'
      ? (raw.configChoices as Record<string, string>)
      : undefined;
  const configSummary = Array.isArray(raw.configSummary)
    ? (raw.configSummary as ConfigSummaryRow[])
    : undefined;
  return {
    id: String(raw.id),
    templateId: raw.templateId != null ? String(raw.templateId) : undefined,
    title: String(raw.title || ''),
    price: String(raw.price || ''),
    oldPrice: String(raw.oldPrice || ''),
    image: String(raw.image || ''),
    qty,
    specs: buildPresetSpecs(raw.specs ? raw.specs : raw),
    configChoices,
    configSummary
  };
}

export function loadCart(): CartLine[] {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const out: CartLine[] = [];
    for (let i = 0; i < parsed.length; i++) {
      const n = normalizeCartItem(parsed[i] as Partial<CartLine>);
      if (n) out.push(n);
    }
    return out;
  } catch {
    return [];
  }
}

function saveCart(items: CartLine[]): void {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* ignore */
  }
}

export function pruneCartAgainstPresets(): boolean {
  try {
    const cart = loadCart();
    if (!cart.length) return false;
    const presetsList = getLastPresetsList();
    if (!Array.isArray(presetsList)) return false;
    if (getPresetsLoadedOnce() && presetsList.length === 0) {
      saveCart([]);
      return true;
    }
    if (presetsList.length === 0) return false;
    const map: Record<string, boolean> = {};
    for (let i = 0; i < presetsList.length; i++) {
      const it = presetsList[i];
      if (it && it.id != null) map[String(it.id)] = true;
    }
    const next = cart.filter((line) => !!map[cartLineTemplateId(line)]);
    if (next.length === cart.length) return false;
    saveCart(next);
    return true;
  } catch {
    return false;
  }
}

function setCartBuyButtonState(btn: HTMLElement | null, inCart: boolean): void {
  if (!btn) return;
  if (inCart) {
    btn.classList.add('preset-cart-btn-in-cart');
    if (!btn.querySelector('.cart-btn-label')) btn.textContent = 'В корзине';
  } else {
    btn.classList.remove('preset-cart-btn-in-cart');
    if (!btn.querySelector('.cart-btn-label')) btn.textContent = 'В корзину';
  }
}

function syncPresetListBuyButtons(): void {
  const listEl = document.getElementById('presets-list');
  if (!listEl) return;
  const ids: Record<string, boolean> = {};
  loadCart().forEach((x) => {
    if (x && x.id != null) ids[cartLineTemplateId(x)] = true;
  });
  listEl.querySelectorAll('.preset-announcement-card[data-preset-id]').forEach((card) => {
    const pid = card.getAttribute('data-preset-id');
    const btn = card.querySelector('.preset-card-buy-btn') as HTMLElement | null;
    if (!btn || btn.classList.contains('preset-card-buy-btn--config')) return;
    setCartBuyButtonState(btn, !!(pid && ids[String(pid)]));
  });
}

function syncDetailCartButton(): void {
  const detailView = document.getElementById('view-preset-detail');
  const cartBtn = document.getElementById('preset-detail-cart-btn');
  if (!detailView || !cartBtn) return;
  const activeId = detailView.getAttribute('data-active-preset-id');
  if (!activeId) return;

  const isConfigurator = detailView.classList.contains('preset-detail--configurator');
  const inCart = loadCart().some((x) => cartLineTemplateId(x) === String(activeId));

  if (!isConfigurator) {
    setCartBuyButtonState(cartBtn, inCart);
  }

  try {
    const bar = document.getElementById('preset-detail-bottom-bar');
    if (bar) {
      if (inCart) bar.classList.add('preset-detail-bottom-bar--in-cart');
      else bar.classList.remove('preset-detail-bottom-bar--in-cart');
      if (isConfigurator) bar.classList.add('preset-detail-bottom-bar--config');
    }
    const goBtn = document.getElementById('preset-detail-go-cart-btn') as HTMLButtonElement | null;
    if (goBtn) {
      const showGoCart = inCart;
      goBtn.setAttribute('aria-hidden', showGoCart ? 'false' : 'true');
      goBtn.disabled = !showGoCart;
    }
    if (!isConfigurator) {
      const a11y = document.getElementById('preset-detail-cart-btn-a11y');
      if (a11y) a11y.textContent = inCart ? 'В корзине' : 'В корзину';
    }
  } catch {
    /* ignore */
  }
}

export function syncPresetCartButtonsUi(): void {
  syncPresetListBuyButtons();
  syncDetailCartButton();
}

export function addToCartFromPreset(item: PresetItem | null | undefined): void {
  if (presetHasConfigurator(item)) return;
  const line = cartLineFromPreset(item);
  if (!line) return;
  const cart = loadCart();
  const exists = cart.some((x) => String(x.id) === line.id);
  if (exists) {
    removeCartLine(line.id);
    return;
  }
  cart.push(line);
  saveCart(cart);
  refreshCartPromoBar();
  syncPresetCartButtonsUi();
}

export interface ConfiguredPresetCartInput {
  lineKey: string;
  templateId: string;
  item: PresetItem;
  priceRub: number;
  configChoices: Record<string, string>;
  configSummary: ConfigSummaryRow[];
  specs: PresetSpecs;
}

export function addConfiguredPresetToCart(input: ConfiguredPresetCartInput): void {
  const imgs = Array.isArray(input.item.images) ? input.item.images.filter(Boolean) : [];
  const img =
    (input.item.image && String(input.item.image)) || (imgs[0] && String(imgs[0])) || '';
  const line: CartLine = {
    id: input.lineKey,
    templateId: input.templateId,
    title: String(input.item.title || ''),
    price: formatRub(input.priceRub),
    oldPrice: String(input.item.oldPrice || ''),
    image: img,
    qty: 1,
    specs: input.specs,
    configChoices: input.configChoices,
    configSummary: input.configSummary
  };
  const cart = loadCart();
  const idx = cart.findIndex((x) => String(x.id) === line.id);
  if (idx >= 0) {
    cart[idx].qty = Math.min(99, (cart[idx].qty || 1) + 1);
  } else {
    cart.push(line);
  }
  saveCart(cart);
  refreshCartPromoBar();
  syncPresetCartButtonsUi();
  try {
    openCartFromPresetDetail();
  } catch {
    /* ignore */
  }
}

function removeCartLine(id: string | number, opts?: { closeOnEmpty?: boolean }): void {
  const cart = loadCart().filter((x) => String(x.id) !== String(id));
  saveCart(cart);
  refreshCartPromoBar();
  renderCartSheetList();
  syncPresetCartButtonsUi();
  if (opts?.closeOnEmpty && cart.length === 0) {
    try {
      cartSheet.close();
    } catch {
      /* ignore */
    }
  }
}

function changeCartLineQty(id: string | number, delta: number): void {
  const cart = loadCart();
  let idx = -1;
  for (let i = 0; i < cart.length; i++) {
    if (String(cart[i].id) === String(id)) {
      idx = i;
      break;
    }
  }
  if (idx < 0) return;
  const curQ = cart[idx].qty || 1;
  if (delta < 0 && curQ <= 1) return;
  const q = curQ + delta;
  if (q < 1) {
    cart.splice(idx, 1);
  } else {
    cart[idx].qty = Math.min(99, q);
  }
  saveCart(cart);
  refreshCartPromoBar();
  renderCartSheetList();
  syncPresetCartButtonsUi();
}

export function clearCart(): void {
  saveCart([]);
  try {
    syncPresetCartButtonsUi();
  } catch {
    /* ignore */
  }
  refreshCartPromoBar();
  try {
    renderCartSheetList();
  } catch {
    /* ignore */
  }
}

export function cartItemsPlural(n: number): string {
  const abs = Math.floor(Math.abs(n)) % 100;
  const d = abs % 10;
  if (abs >= 11 && abs <= 14) return 'товаров';
  if (d === 1) return 'товар';
  if (d >= 2 && d <= 4) return 'товара';
  return 'товаров';
}

export interface CartTotalsRub {
  count: number;
  sum: number;
  allPriced: boolean;
}

export function cartTotalsRub(cart: CartLine[]): CartTotalsRub {
  let count = 0;
  let sum = 0;
  let allPriced = true;
  for (let i = 0; i < cart.length; i++) {
    const line = cart[i];
    const q = line.qty || 1;
    count += q;
    const p = parsePriceNumber(line.price);
    if (p == null) allPriced = false;
    else sum += p * q;
  }
  return { count, sum, allPriced };
}

let cartPromoSuppressUntil = 0;

function isCartPromoPresetsListView(): boolean {
  try {
    const active = document.querySelector('.view-active');
    return !!(active && active.id === 'view-presets');
  } catch {
    return false;
  }
}

export function hideCartPromoBarNow(): void {
  try {
    document.body.classList.remove('cart-promo-visible');
    const bar = document.getElementById('cart-promo-bar');
    if (bar) bar.setAttribute('aria-hidden', 'true');
  } catch {
    /* ignore */
  }
}

export function refreshCartPromoBar(): void {
  const bar = document.getElementById('cart-promo-bar');
  if (!bar) return;
  const now = Date.now();
  if (now < cartPromoSuppressUntil) {
    document.body.classList.remove('cart-promo-visible');
    bar.setAttribute('aria-hidden', 'true');
    return;
  }
  const sheet = document.getElementById('cart-sheet');
  const sheetBlocking =
    sheet &&
    (sheet.classList.contains('sheet-open') || sheet.classList.contains('sheet-closing'));
  const cart = loadCart();
  let n = 0;
  for (let i = 0; i < cart.length; i++) n += cart[i].qty || 1;
  if (n > 0 && !sheetBlocking && isCartPromoPresetsListView()) {
    document.body.classList.add('cart-promo-visible');
    bar.setAttribute('aria-hidden', 'false');
  } else {
    document.body.classList.remove('cart-promo-visible');
    bar.setAttribute('aria-hidden', 'true');
  }
}

const CART_DELETE_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M10 11v6M14 11v6"/></svg>';

function renderCartSheetList(): void {
  const listEl = document.getElementById('cart-sheet-list');
  const emptyEl = document.getElementById('cart-sheet-empty');
  const footerEl = document.getElementById('cart-sheet-footer');
  const labelEl = document.getElementById('cart-summary-label');
  const priceEl = document.getElementById('cart-summary-price');
  if (!listEl) return;
  try {
    pruneCartAgainstPresets();
  } catch {
    /* ignore */
  }
  const cart = loadCart();
  const hasItems = cart.length > 0;
  if (emptyEl) emptyEl.style.display = hasItems ? 'none' : 'block';
  if (footerEl) footerEl.hidden = !hasItems;
  listEl.setAttribute('data-density', hasItems ? 'spacious' : 'normal');

  if (hasItems && labelEl && priceEl) {
    const t = cartTotalsRub(cart);
    labelEl.textContent = 'Итого ' + t.count + ' ' + cartItemsPlural(t.count) + ':';
    priceEl.textContent = t.allPriced ? formatRub(t.sum) : '—';
  }

  listEl.innerHTML = cart
    .map((line) => {
      const price = formatPriceWithRub(line.price);
      const qty = line.qty || 1;
      const thumb = line.image
        ? lazyImageHtml(line.image, { loading: 'lazy' })
        : '<div class="cart-item-thumb-placeholder"></div>';
      const configRows = Array.isArray(line.configSummary) ? line.configSummary : [];
      let configHtml = '';
      if (configRows.length) {
        configHtml = '<ul class="cart-item-config">';
        for (let ci = 0; ci < configRows.length; ci++) {
          const row = configRows[ci];
          if (!row?.value) continue;
          configHtml +=
            '<li><span class="cart-item-config-label">' +
            escapeAttr(row.label) +
            ':</span> ' +
            escapeAttr(row.value) +
            '</li>';
        }
        configHtml += '</ul>';
      }
      return (
        '<div class="cart-item" data-cart-id="' +
        escapeAttr(line.id) +
        '">' +
        '<div class="cart-item-thumb">' +
        thumb +
        '</div>' +
        '<div class="cart-item-col">' +
        '<p class="cart-item-price">' +
        escapeAttr(price || '—') +
        '</p>' +
        '<p class="cart-item-title">' +
        escapeAttr(line.title || '') +
        '</p>' +
        configHtml +
        '<div class="cart-item-actions">' +
        '<button type="button" class="cart-item-delete" aria-label="Удалить">' +
        CART_DELETE_SVG +
        '<span>Удалить</span></button>' +
        '<div class="cart-item-stepper">' +
        '<button type="button" class="cart-stepper-btn"' +
        (qty <= 1 ? ' disabled' : '') +
        ' data-cart-delta="-1" aria-label="Меньше">−</button>' +
        '<span class="cart-stepper-val">' +
        qty +
        '</span>' +
        '<button type="button" class="cart-stepper-btn" data-cart-delta="1" aria-label="Больше">+</button>' +
        '</div></div></div></div>'
      );
    })
    .join('');

  refreshLazyImages(listEl);

  listEl.querySelectorAll('.cart-item-delete').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const row = btn.closest('.cart-item');
      const cid = row?.getAttribute('data-cart-id');
      if (cid) removeCartLine(cid, { closeOnEmpty: true });
    });
  });
  listEl.querySelectorAll('.cart-stepper-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const row = btn.closest('.cart-item');
      const cid = row?.getAttribute('data-cart-id');
      const d = parseInt(btn.getAttribute('data-cart-delta') || '', 10);
      if (cid && d) changeCartLineQty(cid, d);
    });
  });
  refreshCheckoutOrderSummary();
}

export interface CartSheetController {
  open: () => void;
  close: () => void;
}

export function openCartFromPresetDetail(): void {
  prepareForCartNavigation();
  try {
    hideCartPromoBarNow();
  } catch {
    /* ignore */
  }
  try {
    cartSheet.open();
  } catch {
    /* ignore */
  }
}

function presetDetailGoCartReady(): boolean {
  const bar = document.getElementById('preset-detail-bottom-bar');
  const btn = document.getElementById('preset-detail-go-cart-btn') as HTMLButtonElement | null;
  return !!(
    bar &&
    btn &&
    !btn.disabled &&
    bar.classList.contains('preset-detail-bottom-bar--in-cart')
  );
}

function openPresetDetailGoCart(clientY: number): void {
  if (!presetDetailGoCartReady()) return;
  recordCartNavigateGesture(clientY);
  openCartFromPresetDetail();
}

function bindPresetDetailBottomBarCartGuard(): void {
  const bar = document.getElementById('preset-detail-bottom-bar');
  if (!bar || bar.dataset.cartGuardBound === '1') return;
  bar.dataset.cartGuardBound = '1';
  bar.addEventListener(
    'pointerdown',
    (e) => {
      if (!presetDetailGoCartReady()) return;
      recordCartNavigateGesture(e.clientY || window.innerHeight - 48);
    },
    { capture: true, passive: true }
  );
}

export function bindPresetDetailGoCartButton(): void {
  const btn = document.getElementById('preset-detail-go-cart-btn') as HTMLButtonElement | null;
  if (!btn || btn.dataset.goCartBound === '1') return;
  btn.dataset.goCartBound = '1';
  bindPresetDetailBottomBarCartGuard();

  btn.addEventListener(
    'pointerdown',
    (e) => {
      if (!presetDetailGoCartReady()) return;
      recordCartNavigateGesture(e.clientY || window.innerHeight - 48);
    },
    { capture: true, passive: true }
  );

  btn.addEventListener(
    'touchstart',
    (e) => {
      if (!presetDetailGoCartReady()) return;
      const touch = e.touches?.[0];
      recordCartNavigateGesture(touch?.clientY ?? window.innerHeight - 48);
    },
    { capture: true, passive: true }
  );

  btn.addEventListener(
    'touchend',
    (e) => {
      if (!presetDetailGoCartReady()) return;
      e.preventDefault();
      e.stopPropagation();
      const touch = e.changedTouches?.[0];
      openPresetDetailGoCart(touch?.clientY ?? window.innerHeight - 48);
    },
    { capture: true, passive: false }
  );

  btn.addEventListener(
    'click',
    (e) => {
      if (!presetDetailGoCartReady()) return;
      e.preventDefault();
      e.stopPropagation();
      const me = e as MouseEvent;
      openPresetDetailGoCart(me.clientY || window.innerHeight - 48);
    },
    true
  );
}

export function bindCartPromoButton(): void {
  const promoBtn = document.getElementById('cart-promo-open-btn');
  if (!promoBtn || promoBtn.dataset.bound === '1') return;
  promoBtn.dataset.bound = '1';

  promoBtn.addEventListener(
    'touchend',
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const touch = e.changedTouches?.[0];
      recordCartNavigateGesture(touch?.clientY ?? window.innerHeight - 48);
      openCartFromPresetDetail();
    },
    { capture: true, passive: false }
  );

  promoBtn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    const me = e as MouseEvent;
    recordCartNavigateGesture(me.clientY || window.innerHeight - 48);
    openCartFromPresetDetail();
  });
}

export const cartSheet: CartSheetController = (function () {
  let sheet = document.getElementById('cart-sheet');
  let backdrop = document.getElementById('cart-sheet-backdrop');
  let btnBack = document.getElementById('cart-sheet-back');
  let btnCheckout = document.getElementById('cart-sheet-checkout');
  let closeTimer: ReturnType<typeof setTimeout> | null = null;

  function resolveSheetNodes(): void {
    if (!sheet) sheet = document.getElementById('cart-sheet');
    if (!backdrop) backdrop = document.getElementById('cart-sheet-backdrop');
    if (!btnBack) btnBack = document.getElementById('cart-sheet-back');
    if (!btnCheckout) btnCheckout = document.getElementById('cart-sheet-checkout');
  }

  function close(): void {
    resolveSheetNodes();
    if (!sheet) return;
    if (closeTimer) {
      try {
        clearTimeout(closeTimer);
      } catch {
        /* ignore */
      }
      closeTimer = null;
    }
    sheet.classList.remove('sheet-open');
    sheet.classList.add('sheet-closing');
    const sheetEl = sheet;
    closeTimer = setTimeout(() => {
      sheetEl.classList.remove('sheet-closing');
      sheetEl.setAttribute('aria-hidden', 'true');
      try {
        document.body.classList.remove('sheet-raise-app');
      } catch {
        /* ignore */
      }
      closeTimer = null;
      try {
        refreshCartPromoBar();
      } catch {
        /* ignore */
      }
    }, 320);
  }

  function open(): void {
    resolveSheetNodes();
    if (!sheet) return;
    try {
      prepareForCartNavigation();
    } catch {
      /* ignore */
    }
    renderCartSheetList();
    if (closeTimer) {
      try {
        clearTimeout(closeTimer);
      } catch {
        /* ignore */
      }
      closeTimer = null;
    }
    sheet.classList.remove('sheet-closing');
    sheet.classList.add('sheet-open');
    sheet.setAttribute('aria-hidden', 'false');
    try {
      document.body.classList.add('sheet-raise-app');
    } catch {
      /* ignore */
    }
    try {
      refreshCartPromoBar();
    } catch {
      /* ignore */
    }
  }

  if (btnBack) btnBack.addEventListener('click', close);
  if (backdrop) backdrop.addEventListener('click', close);
  if (btnCheckout) {
    btnCheckout.addEventListener('click', () => {
      cartPromoSuppressUntil = Date.now() + 1200;
      try {
        hideCartPromoBarNow();
      } catch {
        /* ignore */
      }
      close();
      setTimeout(() => {
        try {
          preemptTelegramBottomBarForTabbarShow();
          const tabOrderEl = document.getElementById('tab-order');
          setActiveTab(tabOrderEl);
          enterCatalogCheckout();
          showView('view-checkout', 'right');
        } catch {
          /* ignore */
        }
      }, 100);
    });
  }
  return { open, close };
})();

export function initCartUi(): void {
  refreshCartPromoBar();
  bindPresetDetailGoCartButton();
  bindCartPromoButton();
  const cartDeliveryInfoBtn = document.getElementById('cart-delivery-info-btn');
  if (cartDeliveryInfoBtn) {
    cartDeliveryInfoBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      showOrderPopover('Доставка оплачивается отдельно', cartDeliveryInfoBtn);
    });
  }
}
